import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { BarPlot } from '@mui/x-charts/BarChart';
import { LinePlot, MarkPlot, type MarkElementProps } from '@mui/x-charts/LineChart';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { ChartsTooltip, chartsTooltipClasses } from '@mui/x-charts/ChartsTooltip';
import type { AxisConfig, ChartsXAxisProps } from '@mui/x-charts/models';

// Range bar width as a fraction of the plot width (a 7-reading week at a 0.95 gap ratio).
const BAR_WIDTH_FRACTION = 0.05 / 7;

// systolic renders as a filled dot, diastolic as a hollow ring —
// there's no built-in "hollow" mark shape, so this overrides the mark slot for both series.
const RangeMark = ({ id, x = 0, y = 0, color }: MarkElementProps) => {
  const theme = useTheme();
  const isTabletUp = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <circle
      cx={x}
      cy={y}
      r={isTabletUp ? 6 : 5}
      fill={id === 'diastolic' ? '#fff' : color}
      stroke={color}
      strokeWidth={2}
    />
  );
};

const mmHg = (value: number | null) => (value === null ? null : `${value} mmHg`);

// With no readings, an unlabelled week and fixed y range still draw the grid.
const EMPTY_DATES = Array.from({ length: 7 }, (_, index) => `empty-${index}`);
const EMPTY_Y_MAX = 200;

export interface BloodPressureChartProps {
  dates: string[];
  systolic: (number | null)[];
  diastolic: (number | null)[];
  /** Defaults to the parent's width. */
  width?: number;
  height?: number;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
}

// a transparent bar stacked under a visible one
// two dashed line series trace the systolic/diastolic trend on top of it
export const BloodPressureChart = ({
  dates,
  systolic,
  diastolic,
  width,
  height = 300,
  margin,
}: BloodPressureChartProps) => {
  const theme = useTheme();
  const isTabletUp = useMediaQuery(theme.breakpoints.up('sm'));
  const color = theme.palette.primary.main;
  const range = systolic.map((value, index) =>
    value === null || diastolic[index] === null ? null : value - diastolic[index],
  );
  const readings = [...systolic, ...diastolic].filter((value): value is number => value !== null);
  const isEmpty = readings.length === 0;
  const xDates = isEmpty ? EMPTY_DATES : dates;
  // Crop the y-axis to the readings (rounded out to 10s); the bar baseline at 0 would otherwise stretch it.
  // The min is the largest multiple of 10 strictly below the lowest reading, so 80 gets a 70 floor.
  const yMin = isEmpty ? 0 : Math.ceil(Math.min(...readings) / 10) * 10 - 10;
  const yMax = isEmpty ? EMPTY_Y_MAX : Math.ceil(Math.max(...readings) / 10) * 10;
  const hiddenTickLabels = isEmpty ? { valueFormatter: () => '' } : {};
  const tickLabelStyle = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: 400,
    fontSize: isTabletUp ? 16 : 12,
    lineHeight: isTabletUp ? '20px' : '16px',
    letterSpacing: 0,
    fill: theme.palette.grey[800],
  };
  // MUI offsets tick labels by tickSize (+2-3px); the tick marks themselves are hidden below.
  const tickSize = 6;
  const hiddenTick = { axisTick: { style: { display: 'none' } } };

  return (
    <ResponsiveChartContainer
      width={width}
      height={height}
      margin={margin ?? { top: 16, right: 0, bottom: 16, left: 24 }}
      // Let y-axis labels overhang into the card padding so the axis line stays aligned with the title.
      sx={{ overflow: 'visible' }}
      xAxis={[
        {
          id: 'dates',
          data: xDates,
          scaleType: 'band',
          // Each band is plotWidth / dates.length, so scale the gap with the count to keep bar width constant.
          categoryGapRatio: Math.max(0, 1 - BAR_WIDTH_FRACTION * xDates.length),
          tickSize,
          // The default 'auto' hides labels it thinks might collide on narrow widths; always show every date.
          tickLabelInterval: () => true,
          tickLabelStyle,
          ...hiddenTickLabels,
        } as AxisConfig<'band', string, ChartsXAxisProps>,
      ]}
      yAxis={[
        {
          id: 'mmHg',
          min: yMin,
          max: yMax,
          tickNumber: isTabletUp ? 6 : 5,
          tickSize,
          tickLabelStyle,
          ...hiddenTickLabels,
        },
      ]}
      series={[
        {
          type: 'bar',
          id: 'range-base',
          xAxisId: 'dates',
          yAxisId: 'mmHg',
          data: diastolic,
          stack: 'bp',
          color: 'transparent',
          valueFormatter: () => null,
        },
        {
          type: 'bar',
          id: 'range',
          xAxisId: 'dates',
          yAxisId: 'mmHg',
          data: range,
          stack: 'bp',
          color,
          valueFormatter: () => null,
        },
        {
          type: 'line',
          id: 'systolic',
          label: 'Systolic',
          xAxisId: 'dates',
          yAxisId: 'mmHg',
          data: systolic,
          color,
          curve: 'monotoneX',
          valueFormatter: mmHg,
          connectNulls: true,
        },
        {
          type: 'line',
          id: 'diastolic',
          label: 'Diastolic',
          xAxisId: 'dates',
          yAxisId: 'mmHg',
          data: diastolic,
          color,
          curve: 'monotoneX',
          valueFormatter: mmHg,
          connectNulls: true,
        },
      ]}
    >
      <ChartsGrid horizontal />
      {/* ChartsGrid's vertical lines sit between band dates; reference lines sit on each date. */}
      {[...new Set(xDates)].map((date) => (
        <ChartsReferenceLine
          key={date}
          x={date}
          axisId="dates"
          lineStyle={{ stroke: theme.palette.divider }}
        />
      ))}
      <BarPlot />
      <LinePlot slotProps={{ line: { strokeDasharray: '6 4' } }} />
      <MarkPlot slots={{ mark: RangeMark }} />
      <ChartsXAxis axisId="dates" slotProps={hiddenTick} />
      <ChartsYAxis axisId="mmHg" slotProps={hiddenTick} />
      {!isEmpty && (
        <ChartsTooltip
          trigger="axis"
          slotProps={{
            popper: {
              sx: {
                // Diastolic is the last series row; match its hollow chart marker.
                [`& tbody .${chartsTooltipClasses.row}:last-of-type .${chartsTooltipClasses.mark}`]:
                  {
                    background: theme.palette.background.paper,
                    border: `2px solid ${color}`,
                  },
              },
            },
          }}
        />
      )}
    </ResponsiveChartContainer>
  );
};
